import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, FileText, Clock, Calendar, DollarSign, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { EmailHealthBanner } from "@/components/EmailHealthBanner";
import { RecentPayments } from "@/components/RecentPayments";
import { ApiActivityCard } from "@/components/activity/ApiActivityCard";
import { RecurringInvoicesCard } from "@/components/dashboard/RecurringInvoicesCard";
import { usePermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const { can } = usePermissions();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Customers",
      value: stats?.total_customers || 0,
      icon: Users,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      testId: "total-customers-stat"
    },
    {
      title: "Total Invoices",
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      testId: "total-invoices-stat"
    },
    {
      title: "Pending Invoices",
      value: stats?.pending_invoices || 0,
      icon: AlertCircle,
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      testId: "pending-invoices-stat"
    },
    {
      title: "Upcoming Appointments",
      value: stats?.upcoming_appointments || 0,
      icon: Calendar,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      testId: "upcoming-appointments-stat"
    },
    {
      title: "Total Revenue",
      value: `$${(stats?.total_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "bg-emerald-50",
      testId: "total-revenue-stat"
    },
    {
      title: "Pending Amount",
      value: `$${(stats?.pending_amount || 0).toFixed(2)}`,
      icon: Clock,
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      testId: "pending-amount-stat"
    },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Dashboard</h1>
        <p className="text-slate-600">Overview of your business metrics</p>
      </div>

      {can('settings.manage') && <EmailHealthBanner />}

      <RecurringInvoicesCard isAdmin={can('invoices.recurring_manage') || can('invoices.delete')} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={stat.testId}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-slate-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.bgColor} p-2 rounded-lg`}>
                    <Icon className={`w-5 h-5 bg-gradient-to-br ${stat.color} text-white rounded`} style={{padding: '2px'}} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold bg-gradient-to-br ${stat.color} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {can('payments.view') && stats && <RecentPayments stats={stats} />}
        {can('audit.view') && <ApiActivityCard />}
        <Card className="bg-white border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <a href="/customers" className="block p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-200">
              <h3 className="font-semibold text-blue-800 mb-1">Add New Customer</h3>
              <p className="text-sm text-blue-600">Create a new customer record</p>
            </a>
            <a href="/invoices" className="block p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl hover:from-purple-100 hover:to-purple-200 transition-all duration-200">
              <h3 className="font-semibold text-purple-800 mb-1">Create Invoice</h3>
              <p className="text-sm text-purple-600">Generate a new invoice</p>
            </a>
            <a href="/appointments" className="block p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl hover:from-green-100 hover:to-green-200 transition-all duration-200">
              <h3 className="font-semibold text-green-800 mb-1">Schedule Appointment</h3>
              <p className="text-sm text-green-600">Book a new appointment</p>
            </a>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-white">Business Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <span className="text-blue-100">Collection Rate</span>
              <span className="font-bold text-lg">
                {stats?.total_invoices > 0
                  ? ((((stats?.total_invoices - stats?.pending_invoices) / stats?.total_invoices) * 100).toFixed(1))
                  : 0}%
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <span className="text-blue-100">Avg Invoice Value</span>
              <span className="font-bold text-lg">
                ${stats?.total_invoices > 0
                  ? ((stats?.total_revenue / (stats?.total_invoices - stats?.pending_invoices)) || 0).toFixed(2)
                  : '0.00'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <span className="text-blue-100">Active Customers</span>
              <span className="font-bold text-lg">{stats?.total_customers || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
